import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { InvoiceFormFieldValues } from 'modules/Invoices/InvoiceForm/InvoiceForm.types';
import { useSelector } from 'react-redux';
import { invoiceFormToData } from 'shared/serializer';
import type { StoreState } from 'types/storeTypes';
import { InvoiceCreateParamsSchema, type InvoiceResponse } from './invoice-schemas';
import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

type CreateInvoiceVariables = {
  data: InvoiceFormFieldValues;
};

export async function createInvoiceFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateInvoiceVariables,
  constants: StoreState['app']['constants'],
) {
  const instance = await axiosInstance;
  const serialized = invoiceFormToData(variables.data, constants);
  const validated = InvoiceCreateParamsSchema.parse(serialized);
  return instance.post<InvoiceResponse>('/invoices', validated);
}

export function useCreateInvoice(
  options: MutationOptions<AxiosResponse<InvoiceResponse>, ApiError, CreateInvoiceVariables> = {},
) {
  const axiosInstance = useLegacyAxiosInstance();
  const constants = useSelector((state: StoreState) => state.app.constants);
  const queryClient = useQueryClient();

  const mutation = useMutation<AxiosResponse<InvoiceResponse>, ApiError, CreateInvoiceVariables>({
    mutationFn: variables => createInvoiceFn(axiosInstance, variables, constants),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    ...options,
  });

  return addActionNameToMutationResult<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    CreateInvoiceVariables,
    'createInvoice'
  >('createInvoice', mutation);
}
